import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  useEditor,
  useEditorState,
  EditorContent,
  type Editor,
} from '@tiptap/react'
import { mergeAttributes } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import {
  Table,
  TableCell,
  TableHeader,
  TableKit,
  TableRow,
} from '@tiptap/extension-table'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronDown,
  Table as TableIcon,
  Undo2,
  Redo2,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { PlaceholderNode } from './placeholderNode'

export interface RichTemplateEditorHandle {
  insertToken: (token: string) => void
}

type TemplateLayoutMode = 'quote' | 'invoice'

interface RichTemplateEditorProps {
  value: string
  onChange: (storageHtml: string) => void
  placeholder?: string
  layoutMode?: TemplateLayoutMode
}

// Styling of the editable area, split by concern (the whole thing is one class
// attribute on the ProseMirror element). The typography / box / table rules
// deliberately approximate the printable shell the backend wraps a template in
// (DocumentTemplateRenderer::wrap) so what an admin edits matches the PDF.
const editorBaseClass =
  'min-h-[360px] bg-white px-5 py-5 text-[15px] leading-6 text-[#171a20] focus:outline-none'

const editorProseClass =
  '[&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-bold [&_h2]:mb-1.5 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:font-semibold [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_.quote-document]:w-full [&_.quote-document]:text-[15px] [&_.quote-document_td]:p-2 [&_.quote-meta]:border-l [&_.quote-meta]:border-[#d9dfe8] [&_.quote-title-panel]:rounded-md [&_.quote-title-panel]:bg-[#f4f6fb] [&_.quote-title-panel]:p-3 [&_.quote-items]:w-full [&_.quote-items]:border-collapse [&_.quote-items_th]:bg-[#101c70] [&_.quote-items_th]:p-2 [&_.quote-items_th]:text-left [&_.quote-items_th]:text-[10px] [&_.quote-items_th]:text-white [&_.quote-items_td]:border-b [&_.quote-items_td]:border-[#d9dfe8] [&_.quote-items_td]:p-2 [&_.quote-totals]:w-full [&_.quote-totals_td]:border [&_.quote-totals_td]:border-[#d9dfe8] [&_.quote-footer-blocks_td]:border [&_.quote-footer-blocks_td]:border-[#d9dfe8] [&_.quote-footer]:border-t [&_.quote-footer]:border-[#d9dfe8] [&_.quote-footer]:text-center [&_.quote-footer]:text-[9px] [&_.invoice-page-header]:w-full [&_.invoice-page-header]:text-[10px] [&_.invoice-page-header_td]:p-2 [&_.invoice-meta]:border-l [&_.invoice-meta]:border-[#e7e9ef] [&_.invoice-document]:w-full [&_.invoice-document]:text-[11px] [&_.invoice-parties-grid]:w-full [&_.invoice-title-panel]:!table-auto [&_.invoice-title-panel]:rounded-lg [&_.invoice-title-panel]:bg-[#fff1ea] [&_.invoice-title-panel]:p-3 [&_.invoice-title-panel_td:first-child]:whitespace-nowrap [&_.invoice-information]:w-full [&_.invoice-information]:rounded-md [&_.invoice-information]:bg-[#f5f6fa] [&_.invoice-information_td]:p-2 [&_.invoice-items]:w-full [&_.invoice-items]:border-collapse [&_.invoice-items_th]:bg-[#e7e9ef] [&_.invoice-items_th]:p-2 [&_.invoice-items_td]:border-b [&_.invoice-items_td]:border-[#e7e9ef] [&_.invoice-items_td]:p-2 [&_.invoice-totals]:w-full [&_.invoice-total-highlight_td]:bg-[#fff1ea] [&_.invoice-footer-grid]:w-full [&_.invoice-footer-grid_td]:border [&_.invoice-footer-grid_td]:border-[#e7e9ef] [&_.invoice-footer-grid_td]:p-3 [&_.invoice-page-footer]:w-full [&_.invoice-page-footer]:border-t [&_.invoice-page-footer]:border-[#e7e9ef] [&_.invoice-page-footer]:text-center [&_.invoice-page-footer]:text-[9px]'

const editorDocumentLayoutClass =
  '[&_.quote-footer-blocks]:w-full [&_.quote-footer-blocks]:table-fixed [&_.quote-footer-blocks>td]:!border-0 [&_.quote-footer-grid]:w-full [&_.quote-footer-grid]:table-fixed [&_.quote-footer-grid]:!border-separate [&_.quote-footer-grid]:[border-spacing:8px_0] [&_.quote-footer-grid_td]:box-border [&_.quote-footer-grid_td]:overflow-hidden [&_.quote-footer-grid_td]:!border [&_.quote-footer-grid_td]:!border-solid [&_.quote-footer-grid_td]:!border-[#d9dfe8] [&_.quote-footer-grid_td]:p-2'

const editorTitleLayoutClass =
  '[&_.quote-title-panel]:overflow-hidden [&_.quote-title-grid]:w-full [&_.quote-title-grid]:table-fixed [&_.quote-title-grid_td]:box-border [&_.quote-title-grid_td]:overflow-hidden [&_.quote-title-grid_td]:align-middle [&_.quote-title-grid_td:first-child]:!w-[60px] [&_.quote-title-grid_td:first-child]:!min-w-[60px] [&_.quote-title-grid_td:nth-child(2)>p]:!my-0 [&_.quote-title-grid_td:nth-child(2)>p]:flex [&_.quote-title-grid_td:nth-child(2)>p]:flex-col [&_.quote-title-grid_td:nth-child(2)>p]:items-start [&_.quote-title-grid_td:nth-child(2)>p]:gap-1 [&_.invoice-title-panel]:overflow-hidden [&_.invoice-title-grid]:w-full [&_.invoice-title-grid]:table-fixed [&_.invoice-title-grid_td]:box-border [&_.invoice-title-grid_td]:overflow-hidden [&_.invoice-title-grid_td]:align-middle [&_.invoice-title-grid_td:first-child]:!w-[100px] [&_.invoice-title-grid_td:first-child]:!min-w-[100px] [&_.invoice-title-grid_td:nth-child(2)>p]:!my-0 [&_.invoice-title-grid_td:nth-child(2)>p]:flex [&_.invoice-title-grid_td:nth-child(2)>p]:flex-col [&_.invoice-title-grid_td:nth-child(2)>p]:items-start [&_.invoice-title-grid_td:nth-child(2)>p]:gap-1'

// The "Box" tool (blockquote) — bordered, tinted info box with a brand-coloured
// left edge, same as the PDF renders it.
const editorBoxClass =
  '[&_blockquote]:my-3 [&_blockquote]:rounded-xl [&_blockquote]:border [&_blockquote]:border-[#e7e9ef] [&_blockquote]:border-l-4 [&_blockquote]:border-l-[#ff5a1f] [&_blockquote]:bg-[#fbfbfc] [&_blockquote]:px-4 [&_blockquote]:py-3 [&_blockquote_p]:my-1'

// Tables: dashed guides for the layout grid, plus what prosemirror-tables needs
// for column resizing — fixed layout (so explicit widths stick), positioned
// cells, and the drag handle it injects as `.column-resize-handle`.
const editorTableClass =
  '[&_table]:my-3 [&_table]:w-full [&_table]:table-fixed [&_table]:border-collapse [&_.tableWrapper]:overflow-x-auto [&_td]:relative [&_td]:min-w-[3em] [&_td]:border [&_td]:border-dashed [&_td]:border-[#d9dfe8] [&_td]:p-3 [&_td]:align-top [&_th]:relative [&_th]:min-w-[3em] [&_th]:border [&_th]:border-dashed [&_th]:border-[#d9dfe8] [&_th]:bg-[#fbfbfc] [&_th]:p-3 [&_th]:align-top [&_.selectedCell]:bg-[#fff1ea] [&_.column-resize-handle]:pointer-events-none [&_.column-resize-handle]:absolute [&_.column-resize-handle]:-bottom-0.5 [&_.column-resize-handle]:-right-0.5 [&_.column-resize-handle]:top-0 [&_.column-resize-handle]:w-1 [&_.column-resize-handle]:bg-[#ff5a1f] [&.resize-cursor]:cursor-col-resize'

// Stored form: HTML with literal `{{token}}`. Editor form: the same HTML but
// with each `{{token}}` swapped for an empty <span data-placeholder> the
// PlaceholderNode parses into a chip. These two helpers bridge the boundary so
// the backend contract (content = HTML with {{tokens}}) is unchanged.
function fromStorage(content: string): string {
  return (content || '').replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (_m, token) => `<span data-placeholder="${token}"></span>`
  )
}

const compositePlaceholderTokens = new Set([
  'quote_brand',
  'quote_company_block',
  'quote_client_block',
  'line_items',
  'quote_totals_box',
  'bank_details',
  'online_payment',
  'client_signature',
  'footer_legal',
  'invoice_brand',
  'invoice_company_block',
  'invoice_client_block',
  'invoice_information',
  'totals_box',
  'invoice_bank_block',
  'invoice_online_payment_block',
])

function unwrapDirectParagraphs(container: Element): void {
  Array.from(container.children).forEach(child => {
    if (!(child instanceof HTMLParagraphElement)) return
    child.replaceWith(...Array.from(child.childNodes))
  })
}

function isEmptyParagraph(paragraph: HTMLParagraphElement): boolean {
  return (
    paragraph.textContent?.replace(/\u00a0/g, ' ').trim() === '' &&
    !paragraph.querySelector('img, [data-placeholder]')
  )
}

function unwrapCompositePlaceholderParagraphs(root: Element): void {
  root.querySelectorAll('p').forEach(paragraph => {
    const meaningfulNodes = Array.from(paragraph.childNodes).filter(node => {
      return !(
        node.nodeType === Node.TEXT_NODE &&
        (node.textContent ?? '').trim() === ''
      )
    })
    if (meaningfulNodes.length !== 1) return

    const placeholder = meaningfulNodes[0]
    if (!(placeholder instanceof HTMLElement)) return
    const token = placeholder.dataset.placeholder
    if (!token || !compositePlaceholderTokens.has(token)) return

    paragraph.replaceWith(placeholder)
  })
}

function removeEmptyParagraphs(container: Element): void {
  container.querySelectorAll(':scope > p').forEach(paragraph => {
    if (
      paragraph instanceof HTMLParagraphElement &&
      isEmptyParagraph(paragraph)
    ) {
      paragraph.remove()
    }
  })
}

function normalizeSubjectCell(
  document: Document,
  cell: HTMLTableCellElement,
  prefix: TemplateLayoutMode
): void {
  unwrapDirectParagraphs(cell)
  if (cell.querySelector(`:scope > .${prefix}-subject-label`)) return

  const placeholder = cell.querySelector(
    `:scope > [data-placeholder="${prefix}_subject_label"]`
  )
  if (!(placeholder instanceof HTMLElement)) return

  const label = document.createElement('span')
  label.className = `${prefix}-subject-label`
  placeholder.replaceWith(label)
  label.appendChild(placeholder)
}

function normalizeFixedDocumentLayout(
  html: string,
  layoutMode: TemplateLayoutMode
): string {
  if (typeof DOMParser === 'undefined') return html

  const document = new DOMParser().parseFromString(
    `<div data-fixed-document-root>${html}</div>`,
    'text/html'
  )
  const root = document.body.firstElementChild
  if (!(root instanceof HTMLElement)) {
    return html
  }

  root.querySelectorAll('table').forEach(table => {
    table.style.removeProperty('min-width')
    table
      .querySelectorAll(':scope > colgroup')
      .forEach(colgroup => colgroup.remove())
  })
  unwrapCompositePlaceholderParagraphs(root)
  removeEmptyParagraphs(root)

  const headerTable =
    layoutMode === 'quote'
      ? root.querySelector('table.quote-document, table')
      : root.querySelector('table.invoice-page-header')
  if (!(headerTable instanceof HTMLTableElement)) return root.innerHTML

  if (layoutMode === 'quote') {
    headerTable.classList.add('quote-document')
  }
  headerTable.style.width = '100%'
  headerTable.style.tableLayout = 'fixed'

  const headerRow = headerTable.querySelector(':scope > tbody > tr:first-child')
  const cells = headerRow
    ? Array.from(headerRow.children).filter(
        (child): child is HTMLTableCellElement =>
          child instanceof HTMLTableCellElement
      )
    : []

  if (!headerRow || cells.length < 4) return root.innerHTML

  if (layoutMode === 'quote') headerRow.classList.add('quote-header')
  const widths = ['30.2%', '34.2%', '19.4%', '16.2%']
  cells.slice(0, 4).forEach((cell, index) => {
    cell.style.width = widths[index]
    if (index === 0) {
      cell.classList.add(`${layoutMode}-brand`)
      unwrapDirectParagraphs(cell)
      removeEmptyParagraphs(cell)
    } else {
      cell.classList.add(`${layoutMode}-meta`)
      normalizeMetaCell(document, cell)
    }
  })

  const rule = headerTable.querySelector(':scope > tbody > tr:nth-child(2) hr')
  rule?.classList.add(`${layoutMode}-rule`)

  const titleGrid = root.querySelector(`table.${layoutMode}-title-grid`)
  const titleCells = titleGrid?.querySelectorAll(
    ':scope > tbody > tr:first-child > td'
  )
  if (titleCells?.[0]) unwrapDirectParagraphs(titleCells[0])
  if (titleCells?.[1] instanceof HTMLTableCellElement) {
    normalizeSubjectCell(document, titleCells[1], layoutMode)
  }

  if (layoutMode === 'quote') {
    const partiesCell = root.querySelector('tr.quote-parties > td')
    partiesCell
      ?.querySelectorAll(':scope > p')
      .forEach(paragraph => paragraph.classList.add('quote-parties-spacer'))

    root.querySelectorAll('table.quote-footer-grid td').forEach(cell => {
      unwrapDirectParagraphs(cell)
      removeEmptyParagraphs(cell)
    })
  } else {
    root.querySelectorAll('table.invoice-footer-grid td').forEach(cell => {
      removeEmptyParagraphs(cell)
    })
  }

  root
    .querySelectorAll(
      'table.quote-totals-grid td, table.invoice-totals-grid td'
    )
    .forEach(removeEmptyParagraphs)

  return root.innerHTML
}

/**
 * Tiptap table cells only accept block content, so it rewrites the starter's
 * direct `<strong>heading</strong>` as `<p><strong>heading</strong></p>`.
 * The PDF shell deliberately positions the heading and value as two fixed
 * slots. Restore that canonical structure before preview/save; deleting a
 * placeholder then empties its slot without deleting or moving the block.
 */
function normalizeMetaCell(
  document: Document,
  cell: HTMLTableCellElement
): void {
  let heading = cell.querySelector(':scope > strong')
  const paragraphs = Array.from(cell.querySelectorAll(':scope > p'))
  const nestedHeading = paragraphs[0]?.querySelector(':scope > strong')

  if (!heading && nestedHeading instanceof HTMLElement) {
    heading = nestedHeading
    cell.insertBefore(heading, paragraphs[0])

    while (paragraphs[0].firstChild) {
      heading.appendChild(paragraphs[0].firstChild)
    }
    paragraphs[0].remove()
  }

  if (!(heading instanceof HTMLElement)) {
    heading = document.createElement('strong')

    // When all heading placeholders are deleted, Tiptap leaves the empty
    // first paragraph in place. Keep it as the heading slot and preserve the
    // following paragraph as the value slot.
    if (paragraphs.length >= 2) {
      while (paragraphs[0].firstChild) {
        heading.appendChild(paragraphs[0].firstChild)
      }
      paragraphs[0].remove()
    }

    cell.insertBefore(heading, cell.firstChild)
  }

  if (!cell.querySelector(':scope > p')) {
    cell.appendChild(document.createElement('p'))
  }
}

function toStorage(html: string, layoutMode?: TemplateLayoutMode): string {
  const normalizedHtml = layoutMode
    ? normalizeFixedDocumentLayout(html, layoutMode)
    : html
  const storageHtml = normalizedHtml.replace(
    /<span[^>]*\bdata-placeholder="([a-zA-Z0-9_]+)"[^>]*><\/span>/g,
    (_m, token) => `{{${token}}}`
  )

  if (!layoutMode) return storageHtml

  // Tiptap's table renderer adds an editor-only colgroup/min-width model. It
  // is useful for user-created tables, but it overrides the fixed quote shell
  // when the saved HTML is rendered by dompdf.
  return storageHtml
    .replace(/\s*<colgroup>[\s\S]*?<\/colgroup>/gi, '')
    .replace(/\sstyle="min-width:\s*[^";]+;?"/gi, '')
}

// A column resized in the editor is stored by Tiptap in the cell's `colwidth`
// attribute, which dompdf ignores — the PDF would fall back to equal columns.
// Mirror the width into an inline style so the resize survives into the
// generated document (the backend stores the template HTML verbatim).
function inlineWidthStyle(colwidth: unknown): Record<string, string> {
  if (!Array.isArray(colwidth)) return {}
  const total = colwidth.reduce<number>(
    (sum, w) => sum + (typeof w === 'number' ? w : 0),
    0
  )
  return total > 0 ? { style: `width: ${total}px` } : {}
}

const layoutAttributes = {
  class: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute('class'),
    renderHTML: (attributes: { class?: string | null }) =>
      attributes.class ? { class: attributes.class } : {},
  },
  style: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute('style'),
    renderHTML: (attributes: { style?: string | null }) =>
      attributes.style ? { style: attributes.style } : {},
  },
  width: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute('width'),
    renderHTML: (attributes: { width?: string | null }) =>
      attributes.width ? { width: attributes.width } : {},
  },
}

const TableWithClasses = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...layoutAttributes,
    }
  },
})

const TableRowWithClasses = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...layoutAttributes,
    }
  },
})

const TableCellWithWidth = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...layoutAttributes,
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'td',
      mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        inlineWidthStyle(node.attrs.colwidth)
      ),
      0,
    ]
  },
})

const TableHeaderWithWidth = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...layoutAttributes,
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'th',
      mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes,
        inlineWidthStyle(node.attrs.colwidth)
      ),
      0,
    ]
  },
})

type TextAlignValue = 'left' | 'center' | 'right' | null

// Which formatting tools apply at the caret, mirrored into React state so the
// toolbar highlight and the contextual table menu stay in sync with the caret.
interface ToolFlags {
  bold: boolean
  italic: boolean
  heading: boolean
  bulletList: boolean
  orderedList: boolean
  blockquote: boolean
  align: TextAlignValue
  inTable: boolean
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  label,
  children,
}: {
  active?: boolean
  disabled?: boolean
  onClick: () => void
  label: string
  children: ReactNode
}) {
  return (
    <button
      type='button'
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex h-9 w-9 items-center justify-center rounded-lg text-[#5f6673] transition-colors hover:bg-[#fff3ed] hover:text-[#ff5a1f] disabled:opacity-40',
        active && 'bg-[#fff1ea] text-[#ff5a1f]'
      )}
    >
      {children}
    </button>
  )
}

// Table row / column operations. Grouped in a menu with explicit labels so
// "delete row" / "delete column" are discoverable (they have no obvious icon).
// Shown only while the caret sits inside a table, since that is what the
// commands act on.
function TableMenu({ editor }: { editor: Editor }) {
  const { t } = useTranslation('settings')

  const items: { key: string; run: () => void; destructive?: boolean }[] = [
    {
      key: 'addRowBefore',
      run: () => editor.chain().focus().addRowBefore().run(),
    },
    {
      key: 'addRowAfter',
      run: () => editor.chain().focus().addRowAfter().run(),
    },
    {
      key: 'deleteRow',
      run: () => editor.chain().focus().deleteRow().run(),
      destructive: true,
    },
    {
      key: 'addColumnBefore',
      run: () => editor.chain().focus().addColumnBefore().run(),
    },
    {
      key: 'addColumnAfter',
      run: () => editor.chain().focus().addColumnAfter().run(),
    },
    {
      key: 'deleteColumn',
      run: () => editor.chain().focus().deleteColumn().run(),
      destructive: true,
    },
    {
      key: 'deleteTable',
      run: () => editor.chain().focus().deleteTable().run(),
      destructive: true,
    },
  ]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          title={t('templates.editor.tableOptions')}
          aria-label={t('templates.editor.tableOptions')}
          onMouseDown={e => e.preventDefault()}
          className='flex h-9 items-center gap-1 rounded-lg px-2 text-[#5f6673] transition-colors hover:bg-[#fff3ed] hover:text-[#ff5a1f] disabled:opacity-40'
        >
          <TableIcon className='h-4 w-4' />
          <ChevronDown className='h-3 w-3' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='w-56'>
        {items.map(item => (
          <DropdownMenuItem
            key={item.key}
            onSelect={item.run}
            className={cn('text-sm', item.destructive && 'text-destructive')}
          >
            {t(`templates.editor.${item.key}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function Toolbar({
  editor,
  fixedLayout,
}: {
  editor: Editor
  fixedLayout: boolean
}) {
  const { t } = useTranslation('settings')

  // Tiptap 3 no longer re-renders the React tree on every transaction, so
  // reading editor.isActive() during render would go stale as soon as the caret
  // moves without an edit. useEditorState subscribes properly.
  const caret = useEditorState({
    editor,
    selector: ({
      editor,
    }): ToolFlags & { canUndo: boolean; canRedo: boolean } => ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      heading: editor.isActive('heading', { level: 2 }),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      blockquote: editor.isActive('blockquote'),
      align: editor.isActive({ textAlign: 'center' })
        ? 'center'
        : editor.isActive({ textAlign: 'right' })
          ? 'right'
          : editor.isActive({ textAlign: 'left' })
            ? 'left'
            : null,
      inTable: editor.isActive('table'),
      canUndo: editor.can().undo(),
      canRedo: editor.can().redo(),
    }),
  })

  return (
    <div className='flex items-center gap-1 overflow-x-auto border-b border-[#e7e9ef] bg-[#f4f5f8] px-4 py-3'>
      <ToolbarButton
        label={t('templates.editor.bold')}
        active={caret.bold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.italic')}
        active={caret.italic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.heading')}
        active={caret.heading}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.bulletList')}
        active={caret.bulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.orderedList')}
        active={caret.orderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.box')}
        active={caret.blockquote}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className='h-4 w-4' />
      </ToolbarButton>
      <span className='mx-1 h-5 w-px bg-[#eef2f6]' />
      <ToolbarButton
        label={t('templates.editor.alignLeft')}
        active={caret.align === 'left'}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.alignCenter')}
        active={caret.align === 'center'}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.alignRight')}
        active={caret.align === 'right'}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight className='h-4 w-4' />
      </ToolbarButton>
      {!fixedLayout && (
        <>
          <span className='mx-1 h-5 w-px bg-[#eef2f6]' />
          <ToolbarButton
            label={t('templates.editor.insertTable')}
            active={caret.inTable}
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 2, cols: 2, withHeaderRow: false })
                .run()
            }
          >
            <TableIcon className='h-4 w-4' />
          </ToolbarButton>
          {caret.inTable && <TableMenu editor={editor} />}
        </>
      )}
      <span className='mx-1 h-5 w-px bg-[#eef2f6]' />
      <ToolbarButton
        label={t('templates.editor.undo')}
        disabled={!caret.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className='h-4 w-4' />
      </ToolbarButton>
      <ToolbarButton
        label={t('templates.editor.redo')}
        disabled={!caret.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className='h-4 w-4' />
      </ToolbarButton>
    </div>
  )
}

/**
 * WYSIWYG editor for PDF document templates (2.11.14-2.11.18). Hides raw HTML:
 * admins format visually and insert data tokens as non-editable chips. Value in
 * / out is the stored HTML-with-{{tokens}} so the backend contract is unchanged.
 */
export const RichTemplateEditor = forwardRef<
  RichTemplateEditorHandle,
  RichTemplateEditorProps
>(({ value, onChange, placeholder, layoutMode }, ref) => {
  const fixedLayout = layoutMode !== undefined
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      // Resizable columns (drag a column border); the widths round-trip into
      // the PDF via TableCellWithWidth / TableHeaderWithWidth.
      TableKit.configure({
        table: false,
        tableRow: false,
        tableCell: false,
        tableHeader: false,
      }),
      // Tiptap's resizable TableView does not carry our stored table classes
      // into the live DOM. Keep the structured Quote/Invoice shells fixed so
      // their layout selectors (for example invoice-title-grid) still apply.
      TableWithClasses.configure({
        resizable: !fixedLayout,
        cellMinWidth: 40,
      }),
      TableRowWithClasses,
      TableCellWithWidth,
      TableHeaderWithWidth,
      PlaceholderNode,
    ],
    content: fromStorage(value),
    onUpdate: ({ editor }) => onChange(toStorage(editor.getHTML(), layoutMode)),
    editorProps: {
      attributes: {
        class: cn(
          editorBaseClass,
          editorProseClass,
          editorDocumentLayoutClass,
          editorTitleLayoutClass,
          editorBoxClass,
          editorTableClass
        ),
      },
    },
  })

  useImperativeHandle(
    ref,
    () => ({
      insertToken: (token: string) => {
        editor
          ?.chain()
          .focus()
          .insertContent({ type: 'placeholder', attrs: { token } })
          .run()
      },
    }),
    [editor]
  )

  // Sync external value changes (e.g. Create prefills the starter content, or a
  // different template is opened). Guard against feedback loops by comparing the
  // stored form of the current editor content.
  useEffect(() => {
    if (!editor) return
    const current = toStorage(editor.getHTML(), layoutMode)
    if (value !== current) {
      editor.commands.setContent(fromStorage(value), { emitUpdate: false })
    }
  }, [value, editor, layoutMode])

  return (
    <div className='w-full overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white'>
      {editor && <Toolbar editor={editor} fixedLayout={fixedLayout} />}
      <EditorContent
        editor={editor}
        data-placeholder={placeholder}
        className='bg-white'
      />
    </div>
  )
})

RichTemplateEditor.displayName = 'RichTemplateEditor'
